/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { createDefaultGridModel } from '@gh/common-internal-shared';
import {
  AbstractSnapshotFormComponent,
  FormState,
  FormStateBuilder,
  FormStateSnapshot,
  getRouteInitData,
  TextTypes,
  whenCreateOrEditMode,
} from '@gh/common-shared';
import { TranslationService } from '@gh/core-mls';
import { isEditableFormMode } from '@gh/core-ui';
import {
  buildEditableCheckboxColumn,
  buildEditableTextColumn,
  GridDefinition,
  GridDefinitionFactory,
  rowDragMoveEventArrayFactory,
} from '@gh/core-ui-grid';
import { assign } from 'lodash';
import {
  getJobSpecQuoteChangeId,
  JobSpecParagraphDetails,
  JobSpecQuoteChange,
  jobSpecQuoteChangeArr,
  jobSpecQuoteChangeFieldState,
  setJobSpecQuoteChangeField,
  validateJobSpecQuoteChangeField,
} from '../../../resources/job.model';

@Component({
  standalone: false,
  selector: 'gh-job-spec-quote-changes-form',
  templateUrl: 'job-spec-quote-changes-form.component.html',
  styleUrls: ['job-spec-quote-changes-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JobSpecQuoteChangesFormComponent extends AbstractSnapshotFormComponent<JobSpecParagraphDetails>
  implements OnInit {

  definition: GridDefinition;

  @Input() sequence: number;
  // Accept quote changes as Input parameter and send changes via Output
  // It is necessary to guarantee one-way data flow
  @Input() quoteChanges: JobSpecQuoteChange[];

  @Output() quoteChangesChange = new EventEmitter<JobSpecQuoteChange[]>();

  constructor(private route: ActivatedRoute,
              private translations: TranslationService,
              private formStateBuilder: FormStateBuilder,
              private gridDefinitionFactory: GridDefinitionFactory) {
    super();
  }

  ngOnInit(): void {
    super.ngOnInit();

    assign(this, getRouteInitData(this.route));
    this.definition = this.buildGridDefinition();
  }

  protected buildForm(snapshot: FormStateSnapshot<JobSpecParagraphDetails>): FormState<JobSpecParagraphDetails> {
    return this.formStateBuilder.buildPlain({
      mode: this.mode,
      enabled: whenCreateOrEditMode(),
      snapshot,
      children: {
        sections: void 0,
      },
    });
  }

  private buildGridDefinition(): GridDefinition {
    const model = createDefaultGridModel({
      editable: isEditableFormMode(this.mode),
      getId: getJobSpecQuoteChangeId,
      getCellDef: jobSpecQuoteChangeFieldState,
      validate: validateJobSpecQuoteChangeField,
      command: (entity, name, value) => this.setQuoteChangeField(entity, name, value),
    });
    return this.gridDefinitionFactory.buildManualWithModel({
      model,
      columns: [{
        rowDrag: true,
        field: 'name',
        sort: 'asc',
        width: 200,
        maxWidth: 300,
        headerName: this.translations.get('lbl_name'),
      }, {
        field: 'text',
        ...buildEditableTextColumn(TextTypes.Text255, model.getCellAccessor('text')),
        headerName: this.translations.get('lbl_description_and_memo'),
      }, {
        field: 'isHidden',
        width: 80,
        ...buildEditableCheckboxColumn(model.getCellAccessor('isHidden')),
        headerName: this.translations.get('lbl_hide'),
      }],
      options: <any>{
        defaultColDef: {
          sortable: false,
        },
        animateRows: true,
        onRowDragMove: rowDragMoveEventArrayFactory(this.moveSections.bind(this)),
      },
    });
  }

  private moveSections(from: JobSpecQuoteChange, to: JobSpecQuoteChange): void {
    const { quoteChanges } = this;

    const fromIndex = jobSpecQuoteChangeArr.indexOf(quoteChanges, getJobSpecQuoteChangeId(from));
    const toIndex = jobSpecQuoteChangeArr.indexOf(quoteChanges, getJobSpecQuoteChangeId(to));

    this.quoteChangesChange.emit(jobSpecQuoteChangeArr.move(quoteChanges, fromIndex, toIndex));
  }

  private setQuoteChangeField(entity: JobSpecQuoteChange, name: string, value: any): void {
    const { quoteChanges } = this;
    this.quoteChangesChange.emit(jobSpecQuoteChangeArr.update(
      quoteChanges,
      getJobSpecQuoteChangeId(entity),
      setJobSpecQuoteChangeField(entity, name, value)));
  }
}
